import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, Lock } from "lucide-react";

const SOURCES = ["agent", "ad", "entra", "m365"];
const TYPES = ["patch", "mfa", "asset", "security", "backup", "access", "configuration"];

export default function Evidence() {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("agent");
  const [etype, setEtype] = useState("configuration");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/evidence").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const collect = async () => {
    setBusy(true);
    try { await api.post("/evidence/collect", { source, type: etype }); toast.success("Evidence snapshot collected"); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Compliance Evidence Vault</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Audit-Ready Snapshots</h1>
          <p className="text-sm text-zinc-500 mt-1">Tamper-evident evidence collected automatically from APOCALYNX Agent, AD, Entra ID and Microsoft 365.</p>
        </div>
        <div className="flex gap-2 items-end">
          <Select label="Source" value={source} onChange={setSource} options={SOURCES}/>
          <Select label="Type" value={etype} onChange={setEtype} options={TYPES}/>
          <button onClick={collect} disabled={busy} data-testid="evidence-collect-btn" className="bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2">{busy ? "Collecting…" : "Collect snapshot"}</button>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Framework</th><th className="text-left px-4 py-3">Hash</th><th className="text-left px-4 py-3">Collected</th></tr></thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{e.id}</td>
                <td className="px-4 py-2"><span className="text-xs bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 font-mono">{e.source}</span></td>
                <td className="px-4 py-2 text-zinc-300">{e.type}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{e.framework_id || "—"}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-emerald-400 flex items-center gap-1"><Lock size={11}/> {e.hash?.slice(0,12)}…</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{e.collected_at?.slice(0,19).replace("T"," ")}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-sm text-zinc-500">No evidence yet — collect your first snapshot.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
